// The bar's outer buttons: not the game's ends, but the previous and the
// next of the reader's mistakes (blunders and misses, the review API's
// `momentsIn`), and the start or the end only when no mistake lies that way
// (2026-10-13; before, the outer buttons were first and last). Pure, tested.
import { momentLabel, momentsIn, type Moment, type ReviewState } from '@chess/api-review';

export interface OuterStep {
  /** Where the button goes: the position before a mistake was made (the puzzle's, `moment.ply - 1`), or 0 or the last ply. */
  ply: number;
  kind: 'mistake' | 'edge';
  /** Blunder or miss, for the tone, when the stop is a mistake. */
  mistake?: Moment['kind'];
  label: string;
}

export interface OuterSteps {
  back: OuterStep;
  forward: OuterStep;
}

const WORD: Record<Moment['kind'], string> = { blunder: 'blunder', miss: 'miss' };

/** The position a mistake was made from: where its stop lands, and its puzzle's position. */
const positionBefore = (m: Moment): number => m.ply - 1;

/**
 * The two stops for a position of the game (the board's ply, or the ply
 * looked back at): the last mistake made before it and the first made after
 * it, each as the position the mistake was made from, so the reader sees the
 * board the mistake was played on and can look for the better move (2026-10-13;
 * at first the stops were the positions after the moves); the game's start
 * and end when there is none. Undefined without a game.
 */
export function outerSteps(state: ReviewState): OuterSteps | undefined {
  const game = state.game;
  if (state.status !== 'ready' || !game) return undefined;
  const total = game.moves.length;
  const at = state.browse ?? state.ply;
  const moments = momentsIn(state.scan, game.moves, 0, total, state.reader);
  const before = [...moments].reverse().find((m) => positionBefore(m) < at);
  const after = moments.find((m) => positionBefore(m) > at);
  return {
    back: before
      ? { ply: positionBefore(before), kind: 'mistake', mistake: before.kind, label: `Previous ${WORD[before.kind]}: ${momentLabel(before)}` }
      : { ply: 0, kind: 'edge', label: 'Go to the starting position' },
    forward: after
      ? { ply: positionBefore(after), kind: 'mistake', mistake: after.kind, label: `Next ${WORD[after.kind]}: ${momentLabel(after)}` }
      : { ply: total, kind: 'edge', label: 'Go to the final position' },
  };
}
